import React, { useMemo } from 'react';
import { Alert, Button, Col, Input, Result, Row, Select, Space, Table } from 'antd';
import * as Icons from '@ant-design/icons';

import { useBasketTransfer } from '../hooks/use-basket-transfer';
import * as Colors from '@ant-design/colors';
import { useTranslation } from 'react-i18next';
import { filterOption } from '@shared/modules/antd/helpers/filter-option';
import { PageContent } from '@shared/styled/page-content';
import { AcceptProgress, AcceptProgressOuter } from '@modules/quick-accept/styled';

export const BasketTransfer = () => {
  const { t } = useTranslation();

  const {
    currentBasket,
    trackCodes,
    baskets,
    tableData,
    onSelectBasket,
    error,
    submitting,
    onSearch,
    onSubmit,
    onClear,
    onRemove,
    inputRef,
  } = useBasketTransfer();

  const basketSelect = useMemo(
    () => (
      <Select
        showSearch
        allowClear
        onChange={onSelectBasket}
        value={currentBasket}
        filterOption={filterOption}
        style={{ width: 300 }}
        placeholder={t('baskets.choose')}
      >
        {baskets.data.map((basket) => (
          <Select.Option key={basket.id} value={basket.id}>
            {basket.name}
          </Select.Option>
        ))}
      </Select>
    ),
    [baskets.data, currentBasket, onSelectBasket, t],
  );

  const errorAlert = useMemo(
    () =>
      error && (
        <Col span={24}>
          <Alert type='error' showIcon={true} message={error.message} />
        </Col>
      ),
    [error],
  );

  const trackCodeInput = useMemo(
    () => (
      <div style={{ maxWidth: 300, margin: 'auto' }}>
        <AcceptProgressOuter style={{ padding: '24px 0' }}>
          <AcceptProgress width={160} type='circle' percent={trackCodes.length % 100} format={() => trackCodes.length} />
        </AcceptProgressOuter>
        <Input.Search onSearch={onSearch} ref={inputRef} placeholder={t('declarations.enterTrackCode')} />
      </div>
    ),
    [inputRef, onSearch, t, trackCodes.length],
  );

  const trackCodesTable = useMemo(
    () => (
      <Table
        title={() => (
          <Space size={8}>
            <Button loading={submitting} onClick={onSubmit} type='primary' icon={<Icons.CheckCircleOutlined />}>
              {t('common.actions.accept')}
            </Button>
            <Button onClick={onClear} icon={<Icons.DeleteOutlined />} danger>
              {t('common.actions.clear')}
            </Button>
          </Space>
        )}
        rowKey='trackCode'
        size='small'
        bordered
        dataSource={tableData}
      >
        <Table.Column title={t('common.fields.no')} dataIndex='order' key='order' width={75} />
        <Table.Column title={t('declarations.fields.trackCode')} dataIndex='trackCode' key='trackCode' />
        <Table.Column
          title={t('declarations.fields.isInDns')}
          dataIndex='isInDns'
          key='isInDns'
          width={1}
          render={(value, record: any) => {
            if (typeof value === 'boolean') {
              if (!value && record.declared) {
                return (
                  <div style={{ textAlign: 'center' }}>
                    <Icons.LoadingOutlined style={{ color: Colors.blue[5] }} />
                  </div>
                );
              }

              if (value && record.declared) {
                return (
                  <div style={{ textAlign: 'center' }}>
                    <Icons.CheckCircleOutlined style={{ color: Colors.green[5] }} />
                  </div>
                );
              }

              return (
                <div style={{ textAlign: 'center' }}>
                  <Icons.CloseCircleOutlined style={{ color: Colors.red[5] }} />
                </div>
              );
            }

            return null;
          }}
        />
        <Table.Column
          title={t('declarations.fields.declared')}
          dataIndex='declared'
          key='declared'
          width={1}
          render={(value) =>
            typeof value === 'boolean' ? (
              <div style={{ textAlign: 'center' }}>
                {value ? (
                  <Icons.CheckCircleOutlined style={{ color: Colors.green[5] }} />
                ) : (
                  <Icons.CloseCircleOutlined style={{ color: Colors.red[5] }} />
                )}
              </div>
            ) : null
          }
        />
        <Table.Column title={t('declarations.fields.basket')} dataIndex={['basket', 'name']} key='basket.name' />
        <Table.Column title={t('declarations.fields.parcel')} dataIndex={['parcel', 'id']} key='parcel.id' />
        <Table.Column title={t('common.branch')} dataIndex={['branch', 'name']} key='branch.id' />
        <Table.Column title={t('declarations.fields.flightName')} dataIndex='flightName' key='flightName' />
        <Table.Column
          width={1}
          key='remove'
          render={(_, record: any) => {
            return <Button onClick={() => onRemove(record.trackCode)} danger size='small' icon={<Icons.DeleteOutlined />} />;
          }}
        />
      </Table>
    ),
    [onClear, onRemove, onSubmit, submitting, t, tableData],
  );

  return (
    <PageContent title={basketSelect}>
      {!currentBasket && <Result status='500' title={t('baskets.choose')} subTitle={t('baskets.basketRequirement')} />}
      {!!currentBasket && (
        <Row>
          {errorAlert}
          <Col span={24} lg={12}>
            {trackCodeInput}
          </Col>
          <Col span={24} lg={12}>
            {trackCodesTable}
          </Col>
        </Row>
      )}
    </PageContent>
  );
};
